import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TouchableOpacity,
  FlatList,
  useWindowDimensions,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { AuthLayout } from '@/components/AuthLayout';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { ErrorMessage } from '@/components/ErrorMessage';
import { Logo } from '@/components/Logo';
import { TextLink } from '@/components/TextLink';
import { AuthFooter } from '@/components/AuthFooter';
import { colors } from '@/styles/colors';
import { ROUTES, EXTERNAL_URLS, ERROR_MESSAGES } from '@/utils/constants';
import { fetchOrganizations, generateSamlUrl, getSpecialSSOUrl } from '@/api/sso';
import { Organization } from '@/types';

export default function SSOLoginScreen() {
  const router = useRouter();
  const params = useLocalSearchParams();
  const { width } = useWindowDimensions();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [suggestions, setSuggestions] = useState<Organization[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);

  const nextUrl = typeof params.next === 'string' ? params.next : undefined;
  const isDesktop = width >= 768;

  useEffect(() => {
    // Load organizations when component mounts (will use cache if available)
    const loadOrganizations = async () => {
      setOrganizationsLoading(true);
      try {
        const orgs = await fetchOrganizations();
        setOrganizations(orgs);
      } catch (err) {
        setError('Failed to load organizations');
      } finally {
        setOrganizationsLoading(false);
      }
    };

    loadOrganizations();
  }, []);

  const handleOrganizationSearch = (text: string) => {
    setSearchTerm(text);
    setSelectedOrg(null);
    
    if (text.trim() === '') {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (organizations.length > 0) {
      const filtered = organizations.filter((org) =>
        org.name.toLowerCase().includes(text.toLowerCase())
      );
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectOrganization = (org: Organization) => {
    setSearchTerm(org.name);
    setSelectedOrg(org);
    setShowSuggestions(false);
    setError(null);
  };

  const handleSubmit = async () => {
    setError(null);

    if (!selectedOrg) {
      setError('Please select an organization');
      return;
    }

    if (!selectedOrg.sso_url) {
      setError(ERROR_MESSAGES.SSO_NOT_SUPPORTED);
      return;
    }

    setLoading(true);

    try {
      const specialUrl = getSpecialSSOUrl(nextUrl);
      const ssoUrl = specialUrl
        ? generateSamlUrl(specialUrl, nextUrl)
        : generateSamlUrl(selectedOrg.sso_url, nextUrl);

      if (Platform.OS === 'web') {
        window.location.href = ssoUrl;
      }
    } catch (err: any) {
      setError('Failed to redirect to SSO provider. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    router.push(ROUTES.LOGIN);
  };

  return (
    <AuthLayout>
      <View style={styles.formContainer}>
        <Logo />
        
        <View style={styles.formContent}>
          <Text style={styles.heading}>Which organization are you eligible with?</Text>

          <View style={styles.topNote}>
            <Text style={styles.topNoteText}>Need some help? </Text>
            <TextLink href={EXTERNAL_URLS.SUPPORT}>Contact us.</TextLink>
          </View>

          {error && <ErrorMessage message={error} />}

          <View style={styles.inputContainer}>
            <Input
              label="Organization"
              value={searchTerm}
              onChangeText={handleOrganizationSearch}
              placeholder="Your organization"
              maxLength={254}
              autoComplete="organization"
              onFocus={() => searchTerm && setShowSuggestions(true)}
              loading={organizationsLoading}
              containerStyle={styles.organizationInput}
            />

            {showSuggestions && suggestions.length > 0 && (
              <View style={styles.suggestionsWrapper}>
                <FlatList
                  data={suggestions}
                  keyExtractor={(item, index) => `${item.uuid}-${index}`}
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      style={styles.suggestionItem}
                      onPress={() => handleSelectOrganization(item)}
                    >
                      <Text style={styles.suggestionText}>{item.name}</Text>
                    </TouchableOpacity>
                  )}
                  style={styles.suggestionsList}
                  nestedScrollEnabled
                />
              </View>
            )}
            
            {showSuggestions && suggestions.length === 0 && searchTerm.trim() !== '' && !organizationsLoading && (
              <View style={styles.suggestionsWrapper}>
                <View style={styles.suggestionError}>
                  <Text style={styles.suggestionErrorText}>Nothing found.</Text>
                </View>
              </View>
            )}
          </View>

          <View style={styles.formFooter}>
            <View style={isDesktop && styles.buttonContainer}>
              <Button
                title="Continue"
                onPress={handleSubmit}
                loading={loading}
                disabled={!selectedOrg}
              />
            </View>

            <AuthFooter
              variant="sso"
              onBackToLogin={handleBackToLogin}
              onForgotPassword={() => router.push(ROUTES.FORGOT_PASSWORD)}
            />
          </View>
        </View>
      </View>
    </AuthLayout>
  );
}

const styles = StyleSheet.create({
  formContainer: {
    width: '100%',
  },
  formContent: {
    width: '100%',
  },
  heading: {
    fontSize: 40,
    lineHeight: 48,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 8,
  },
  topNote: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  topNoteText: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  inputContainer: {
    position: 'relative',
    zIndex: 10,
    marginBottom: 0,
  },
  organizationInput: {
    marginBottom: 0,
  },
  suggestionsWrapper: {
    position: 'absolute',
    top: 72,
    left: 0,
    right: 0,
    backgroundColor: colors.background,
    borderRadius: 8,
    marginTop: 4,
    maxHeight: 200,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 3,
    zIndex: 1000,
    borderWidth: 1,
    borderColor: colors.border,
  },
  suggestionsList: {
    maxHeight: 200,
  },
  suggestionItem: {
    padding: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  suggestionText: {
    fontSize: 16,
    color: colors.textPrimary,
  },
  suggestionError: {
    padding: 16,
    alignItems: 'center',
  },
  suggestionErrorText: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  formFooter: {
    marginTop: 24,
    gap: 16,
  },
  buttonContainer: {
    alignItems: 'flex-start',
  },
});
